import { Component, OnInit, AfterViewInit, OnDestroy, ChangeDetectorRef, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { LibraryModule } from "../../app/shared/lib/lib.module";
import { Router } from "@angular/router";
import { DateSelectionService } from "../services/date-selection.service";
import { MealService } from "../core/services/meal.service";
import { UserService } from "../core/services/user.service";
import { ActivityService } from "../core/services/activity.service";
import { LanguageService } from "../core/services/language.service";
import { generateDateOptions, getUserCount as getUserCountCommon, getMealData as getMealDataCommon, DateOptionWithBadge, applyMealMenusToDateOptions } from "../shared/utils/commenFunctions";
import { FormDateUtils, HOUSTON_TIME_ZONE } from "../shared/utils/form-date-utils";
import { MealDataResponse, UserCountResponse, UserDetails, MealDataItem, MealPreference } from "../core/models/meal.model";
import { pageLoadAnimationBottomToCenter } from "../shared/utils/common-animations";
import { getUserDetails, getProgramId } from "../shared/utils/storage-utils";
import { MealMenuAdminService } from "../core/services/meal-menu-admin.service";
import { Subscription } from "rxjs";

type DateCardState = "today" | "selected" | "default";

type DateCardOption = DateOptionWithBadge & { 
  submitted: boolean;
  state: DateCardState;
  hotMenuText?: string;
  coldMenuText?: string;
  mealType?: string;
  transport?: string;
  mealScheduled?: boolean;
  transportScheduled?: boolean;
};

interface PrebookingDate {
  dayLabel: string;
  dateLabel: string;
  dateIndex: number;
  dateValue?: string;
  badgeCount?: number;
  state?: DateCardState;
}

interface PlanSummaryRow {
  index: number;
  dateLabel: string;
  dayLabel: string;
  dateValue: string;
  isToday: boolean;
  activitiesLabel: string;
  activitiesScheduled: boolean;
  mealScheduled: boolean;
  transportScheduled: boolean;
}

@Component({
  selector: "success-meal-transport-component, SuccessMealTransportComponent",
  standalone: true,
  templateUrl: './success-meal-transport.component.html',
  styleUrls: ['./success-meal-transport.component.css'],
  imports: [CommonModule, LibraryModule],
  animations: [pageLoadAnimationBottomToCenter]
})
export class SuccessMealTransportComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly languageService = inject(LanguageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private languageSub?: Subscription;

  scheduledDateIndex: number | null = null;
  scheduledDateLabel: string = "";
  scheduledDayLabel: string = "";
  todayDate: string = "";
  formattedScheduledDate: string = "";

  // Date options matching the date-selection component structure
  dateOptions: DateCardOption[] = [];

  // Prebooking dates with their corresponding indices
  prebookingDates: PrebookingDate[] = [];
  mealData: MealDataResponse | null = null;
  userDetails: UserDetails = getUserDetails();
  userCount: UserCountResponse | null = null;
  pageLoadState = 'hidden';
  mealCount: number = 0;
  private todayDateValue = '';
  private mealMenus: Array<{
    day?: string | null;
    hotMeal?: string | null;
    coldMeal?: string | null;
    specialEvents?: string | null;
  }> = [];
  isTodaySelected: boolean = false;
  hasFutureDaysBooked: boolean = false;
  allDaysNotBooked: boolean = true;
  bookedFutureDays: string[] = [];
  /** Senior Center: transport UI only when transportation is approved (not when not_required). */
  isTransportationApproved = false;
  /** Submitted activity names for today (shown in plan summary). */
  private todayActivityNames: string[] = [];

  /**
   * Edit from success summary: keep already-submitted date cards visible
   * (including today) so the participant can re-open and change that day.
   */
  isEditingFromSuccess = false;
  private editTargetDateValue = '';

  /** Same-day / waitlisted reservation (count > 0 matches scheduled-confirmed waitlist pattern). */
  get isTodayOnWaitlist(): boolean {
    return this.isTodaySelected && this.mealCount > 0;
  }

  /** Date cards section: normal prebook list, or full editable list when editing from success. */
  get showDateCardSection(): boolean {
    if (this.isEditingFromSuccess) {
      return this.dateOptions.length > 0;
    }
    // Match pre-check-in: keep the row when today is booked so staff can reopen today.
    if (this.isTodaySelected && this.dateOptions.length > 0) {
      return true;
    }
    return this.prebookingDates.length > 0 || this.dateOptions.some((o) => !o.submitted);
  }

  get canShowTransport(): boolean {
    if (getProgramId() === 1000) {
      return this.isTransportationApproved;
    }
    return true;
  }

  /** Plan summary rows for Edit Plan table (today + upcoming). */
  get planSummaryRows(): PlanSummaryRow[] {
    return this.dateOptions
      .map((option, index) => {
        const isToday = option.dateValue === this.todayDateValue;
        const activitiesLabel =
          isToday && this.todayActivityNames.length > 0
            ? this.todayActivityNames.join(', ')
            : 'None';
        return {
          index,
          dateLabel: option.dateLabel,
          dayLabel: option.dayLabel,
          dateValue: option.dateValue ?? '',
          isToday,
          activitiesLabel,
          activitiesScheduled: isToday && this.todayActivityNames.length > 0,
          mealScheduled: !!option.mealScheduled,
          transportScheduled: !!option.transportScheduled,
        };
      })
      .filter((row) => !!row.dateValue);
  }

  get todayStatusMessage(): string {
    if (this.isTodayOnWaitlist) {
      return this.canShowTransport
        ? this.languageService.t('success.waitlistBoth')
        : this.languageService.t('success.waitlistMeal');
    }
    return this.canShowTransport
      ? this.languageService.t('success.confirmedBoth')
      : this.languageService.t('success.confirmedMeal');
  }

  constructor(
    private readonly router: Router,
    private readonly dateSelectionService: DateSelectionService,
    private readonly mealService: MealService,
    private readonly userService: UserService,
    private readonly mealMenuAdminService: MealMenuAdminService,
    private readonly activityService: ActivityService,
  ) {
    // Format today's date in Houston: "Monday 11, December"
    this.todayDate = FormDateUtils.formatHoustonLongDate();
  }

  ngOnInit(): void {
    this.languageSub = this.languageService.language$.subscribe(() => this.cdr.markForCheck());
    this.isEditingFromSuccess = sessionStorage.getItem('editFromSuccess') === 'true';
    this.editTargetDateValue = (sessionStorage.getItem('dateValue') || '').trim();
    // Generate date options dynamically (today + 3 more days)
    this.dateOptions = generateDateOptions().map((option, index) => ({
      ...option,
      submitted: false,
      state: index === 0 ? 'today' : 'default' as DateCardState,
      hotMenuText: '',
      coldMenuText: '',
      mealType: '',
      transport: '',
      mealScheduled: false,
      transportScheduled: false,
    }));
    this.loadMealMenus();
    this.loadTodayActivities();
    this.todayDateValue = this.dateOptions[0]?.dateValue ?? '';
    this.getMealData();
  }

  /**
   * Pre-check-in always shows today (even when booked) so preference can be changed.
   * Staff check-in matches that: always show today; hide other submitted future days.
   */
  shouldShowDateCard(option: DateCardOption): boolean {
    if (!option) {
      return false;
    }
    if (this.isEditingFromSuccess) {
      return true;
    }
    if (option.dateValue && option.dateValue === this.todayDateValue) {
      return true;
    }
    return !option.submitted;
  }

  ngOnDestroy(): void {
    this.languageSub?.unsubscribe();
  }

  ngAfterViewInit(): void {
    // Trigger animation immediately after view init
    // Use a small delay to ensure Angular has processed the initial state
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.pageLoadState = 'visible';
      }, 50);
    });
  }

  private getMealData(): void {
    getMealDataCommon(
      this.mealService,
      String(this.userDetails.value ?? ''),
      this.dateOptions,
      (response: MealDataResponse) => {
        this.mealData = response;
        this.isTransportationApproved =
          getProgramId() === 1000
            ? response?.isTransportationApproved === true
            : true;
        // Process meal data based on API response
        this.processMealData();
      },
      () => {
        this.mealData = null;
        this.isTransportationApproved = getProgramId() !== 1000;
        // Fallback: if no meal data found, use today
        this.setDefaultScheduledDate();
      }
    );
  }

  private processMealData(): void {
    if (!this.mealData) {
      this.setDefaultScheduledDate();
      return;
    }

    const dateMaps = this.extractDateMapsFromMealData();
    this.updateDateOptionsWithMealInfo(dateMaps);
    this.updateTodayDateValue();
    this.updateTodaySelectedStatus(dateMaps.datesWithFullSchedule);
    
    const scheduledDateIndices = this.findScheduledDateIndices(dateMaps.datesWithFullSchedule);
    this.updatePrebookingDates();
    
    if (scheduledDateIndices.length > 0) {
      this.setScheduledDateFromIndices(scheduledDateIndices, dateMaps.mealCountMap);
    } else {
      this.handleNoScheduledDates();
      return;
    }

    this.getUserCount();
  }

  private extractDateMapsFromMealData(): {
    datesWithFullSchedule: Set<string>;
    mealDates: Set<string>;
    deliveredDates: Set<string>;
    mealCountMap: Map<string, number>;
    locationNameMap: Map<string, string>;
    mealChoiceMap: Map<string, { mealType: string; transport: string; mealScheduled: boolean; transportScheduled: boolean }>;
  } {
    const datesWithFullSchedule = new Set<string>();
    const mealDates = new Set<string>();
    const deliveredDates = new Set<string>();
    const mealCountMap = new Map<string, number>();
    const locationNameMap = new Map<string, string>();
    const mealChoiceMap = new Map<string, { mealType: string; transport: string; mealScheduled: boolean; transportScheduled: boolean }>();

    if (this.mealData?.results && Array.isArray(this.mealData.results)) {
      this.processMealDataItems(this.mealData.results, datesWithFullSchedule, mealDates, deliveredDates, mealCountMap, locationNameMap, mealChoiceMap);
    } else if (Array.isArray(this.mealData)) {
      this.processMealDataArray(this.mealData as unknown as MealDataItem[], datesWithFullSchedule, mealDates, deliveredDates, mealCountMap, locationNameMap, mealChoiceMap);
    }

    return { datesWithFullSchedule, mealDates, deliveredDates, mealCountMap, locationNameMap, mealChoiceMap };
  }

  private processMealDataItems(
    items: MealDataItem[],
    datesWithFullSchedule: Set<string>,
    mealDates: Set<string>,
    deliveredDates: Set<string>,
    mealCountMap: Map<string, number>,
    locationNameMap: Map<string, string>,
    mealChoiceMap: Map<string, { mealType: string; transport: string; mealScheduled: boolean; transportScheduled: boolean }>
  ): void {
    items.forEach((item: MealDataItem) => {
      if (item.mealDate) {
        this.processMealDataItem(item, item.mealDate, datesWithFullSchedule, mealDates, deliveredDates, mealCountMap, locationNameMap, mealChoiceMap);
      }
    });
  }

  private processMealDataArray(
    items: MealDataItem[],
    datesWithFullSchedule: Set<string>,
    mealDates: Set<string>,
    deliveredDates: Set<string>,
    mealCountMap: Map<string, number>,
    locationNameMap: Map<string, string>,
    mealChoiceMap: Map<string, { mealType: string; transport: string; mealScheduled: boolean; transportScheduled: boolean }>
  ): void {
    items.forEach((item: MealDataItem) => {
      const dateValue = item.mealDate ?? item.date;
      if (dateValue) {
        this.processMealDataItem(item, dateValue, datesWithFullSchedule, mealDates, deliveredDates, mealCountMap, locationNameMap, mealChoiceMap);
      }
    });
  }

  private processMealDataItem(
    item: MealDataItem,
    dateValue: string,
    datesWithFullSchedule: Set<string>,
    mealDates: Set<string>,
    deliveredDates: Set<string>,
    mealCountMap: Map<string, number>,
    locationNameMap: Map<string, string>,
    mealChoiceMap: Map<string, { mealType: string; transport: string; mealScheduled: boolean; transportScheduled: boolean }>
  ): void {
    mealCountMap.set(dateValue, item.count ?? 0);
    if (item.locationName) {
      locationNameMap.set(dateValue, item.locationName);
    }
    
    if (this.isDelivered(item)) {
      deliveredDates.add(dateValue);
    }

    const choice = this.parseMealTransportChoice(item.mealPreference);
    mealChoiceMap.set(dateValue, choice);
    
    // Only treat dates with an actual meal preference as selected/booked.
    // Cleared/deselected dates may still have an empty meal_delivery row.
    if (this.hasFullSchedule(item)) {
      datesWithFullSchedule.add(dateValue);
      mealDates.add(dateValue);
    }
    
    if (item.submitted) {
      this.markDateOptionAsSubmitted(dateValue);
    }
  }

  private parseMealTransportChoice(preferences: MealPreference[] | undefined): {
    mealType: string;
    transport: string;
    mealScheduled: boolean;
    transportScheduled: boolean;
  } {
    let mealType = '';
    let transport = '';
    if (Array.isArray(preferences)) {
      for (const pref of preferences) {
        if (pref.preference) {
          mealType = pref.preference;
        }
        if (pref.transport) {
          transport = pref.transport;
        }
      }
    }
    const transportNorm = transport.trim().toLowerCase();
    const transportScheduled =
      !!transport &&
      transportNorm !== '' &&
      transportNorm !== 'none' &&
      transportNorm !== 'no need' &&
      transportNorm !== 'not required' &&
      transportNorm !== 'notrequired';
    const hasMealChoice = !!mealType && mealType.trim() !== '';
    return {
      mealType,
      transport,
      mealScheduled: hasMealChoice && mealType.trim().toLowerCase() !== 'no meal',
      transportScheduled,
    };
  }

  private isDelivered(item: MealDataItem): boolean {
    return item.deliveryStatus?.toLowerCase() === 'delivered';
  }

  private hasFullSchedule(item: MealDataItem): boolean {
    if (!item?.mealPreference || !Array.isArray(item.mealPreference)) {
      return false;
    }
    const hasNoMeal = item.mealPreference.some(
      (pref: MealPreference) => (pref.preference ?? '').trim().toLowerCase() === 'no meal'
    );
    if (hasNoMeal) {
      return true;
    }
    const hasMeal = item.mealPreference.some((pref: MealPreference) => pref.preference);
    // When transport UI is hidden (not required / not approved), meal-only counts as scheduled.
    if (!this.canShowTransport) {
      return !!hasMeal;
    }
    const hasTransport = item.mealPreference.some(
      (pref: MealPreference) =>
        pref.transport &&
        pref.transport !== 'none' &&
        pref.transport !== 'No Need' &&
        pref.transport !== 'Not Required' &&
        pref.transport !== 'Not required'
    );
    // Transport "Not Required" still counts as a complete meal booking when a meal is selected.
    const hasTransportChoice = item.mealPreference.some(
      (pref: MealPreference) => !!pref.transport && pref.transport.trim() !== '' && pref.transport !== 'none'
    );
    return !!(hasMeal && (hasTransport || hasTransportChoice));
  }

  private markDateOptionAsSubmitted(dateValue: string): void {
    this.dateOptions.forEach(option => {
      if (option.dateValue === dateValue) {
        option.submitted = true;
      }
    });
  }

  private updateDateOptionsWithMealInfo(dateMaps: {
    mealDates: Set<string>;
    deliveredDates: Set<string>;
    mealCountMap: Map<string, number>;
    locationNameMap: Map<string, string>;
    mealChoiceMap: Map<string, { mealType: string; transport: string; mealScheduled: boolean; transportScheduled: boolean }>;
  }): void {
    this.dateOptions = this.dateOptions
      .filter(option => !(option.dateValue && dateMaps.deliveredDates.has(option.dateValue)))
      .map(option =>
        this.updateDateOptionState(
          option,
          dateMaps.mealDates,
          dateMaps.mealCountMap,
          dateMaps.locationNameMap,
          dateMaps.mealChoiceMap
        )
      );
    this.applyMenusToCards();
  }

  private mealDietPreference: string | null = null;

  private loadMealMenus(): void {
    const userId = Number(this.userDetails?.value);
    this.mealMenuAdminService
      .listMenus(Number.isFinite(userId) && userId > 0 ? userId : null)
      .subscribe({
        next: (response) => {
          this.mealMenus = response?.menus ?? [];
          this.mealDietPreference = response?.mealDietPreference ?? null;
          this.applyMenusToCards();
        },
        error: () => {
          this.mealMenus = [];
        },
      });
  }

  private loadTodayActivities(): void {
    const userId = Number(this.userDetails?.value);
    if (!Number.isFinite(userId) || userId <= 0) {
      this.todayActivityNames = [];
      return;
    }
    this.activityService
      .getActivities({
        index: 0,
        limit: 100,
        userid: userId,
        date: FormDateUtils.formatHoustonIsoDate(),
      })
      .subscribe({
        next: (response) => {
          if (response?.statusCode !== 200 || !Array.isArray(response.activities)) {
            this.todayActivityNames = [];
            return;
          }
          this.todayActivityNames = response.activities
            .filter((activity) => (activity as { isSubmitted?: boolean }).isSubmitted === true)
            .map((activity) => String(activity.activityName ?? '').trim())
            .filter((name) => !!name);
        },
        error: () => {
          this.todayActivityNames = [];
        },
      });
  }

  private applyMenusToCards(): void {
    if (!this.mealMenus.length) {
      return;
    }
    this.dateOptions = applyMealMenusToDateOptions(
      this.dateOptions,
      this.mealMenus,
      this.mealDietPreference,
    );
  }

  private updateDateOptionState(
    option: DateCardOption,
    mealDates: Set<string>,
    mealCountMap: Map<string, number>,
    locationNameMap: Map<string, string>,
    mealChoiceMap: Map<string, { mealType: string; transport: string; mealScheduled: boolean; transportScheduled: boolean }>
  ): DateCardOption {
    const hasMealInfo = option.dateValue && mealDates.has(option.dateValue);
    const isToday = option.dateValue === this.todayDateValue;

    let newState: DateCardState;
    if (isToday) {
      newState = hasMealInfo ? 'selected' : 'today';
    } else {
      newState = hasMealInfo ? 'selected' : 'default';
    }

    const badgeCount = hasMealInfo && mealCountMap.has(option.dateValue)
      ? mealCountMap.get(option.dateValue)!
      : option.badgeCount ?? 0;

    const locationName = option.dateValue ? (locationNameMap.get(option.dateValue) ?? '') : '';
    const choice = option.dateValue ? mealChoiceMap.get(option.dateValue) : undefined;

    return {
      ...option,
      state: newState,
      badgeCount: badgeCount,
      locationName: locationName,
      mealType: choice?.mealType ?? '',
      transport: choice?.transport ?? '',
      mealScheduled: choice?.mealScheduled ?? false,
      transportScheduled: choice?.transportScheduled ?? false,
    };
  }

  private updateTodayDateValue(): void {
    this.todayDateValue = this.dateOptions.find(option => option.dayLabel === 'Today')?.dateValue ?? this.todayDateValue;
  }

  private updateTodaySelectedStatus(datesWithFullSchedule: Set<string>): void {
    this.isTodaySelected = !!(this.todayDateValue && datesWithFullSchedule.has(this.todayDateValue));
    this.updateBookingStatus(datesWithFullSchedule);
  }

  private updateBookingStatus(datesWithFullSchedule: Set<string>): void {
    // Collect booked future days (not today)
    const bookedDays: string[] = [];
    this.dateOptions.forEach(option => {
      if (option.dateValue && 
          option.dateValue !== this.todayDateValue && 
          datesWithFullSchedule.has(option.dateValue)) {
        bookedDays.push(option.dayLabel);
      }
    });
    
    // Check if any future days (not today) are booked
    const futureDaysBooked = bookedDays.length > 0;
    
    // Check if all days are not booked (check all dates from meal data)
    const allDaysNotBooked = datesWithFullSchedule.size === 0;
    this.hasFutureDaysBooked = futureDaysBooked;
    this.allDaysNotBooked = allDaysNotBooked;
    this.bookedFutureDays = bookedDays;
  }

  private findScheduledDateIndices(datesWithFullSchedule: Set<string>): number[] {
    const scheduledDateIndices: number[] = [];
    this.dateOptions.forEach((option, index) => {
      if (option.dateValue && datesWithFullSchedule.has(option.dateValue)) {
        scheduledDateIndices.push(index);
      }
    });
    return scheduledDateIndices;
  }

  private updatePrebookingDates(): void {
    this.prebookingDates = this.dateOptions
      .filter((option) => {
        if (this.isEditingFromSuccess) {
          // Edit mode: include today + future (submitted or not) so Oct 9 stays clickable.
          return !!option.dateValue;
        }
        // Include today always (match pre-check-in); other days only if not yet submitted.
        if (option.dateValue === this.todayDateValue) {
          return true;
        }
        return !option.submitted;
      })
      .map((option, idx) => {
        const originalIndex = this.dateOptions.findIndex(opt => opt.dateValue === option.dateValue);
        return {
          dayLabel: option.dayLabel,
          dateLabel: option.dateLabel,
          dateIndex: originalIndex !== -1 ? originalIndex : idx,
          dateValue: option.dateValue,
          badgeCount: option.badgeCount,
          state: option.state
        };
      });
  }

  private setScheduledDateFromIndices(scheduledDateIndices: number[], mealCountMap: Map<string, number>): void {
    const todayIndex = this.dateOptions.findIndex(option => option.dateValue === this.todayDateValue);
    
    if (todayIndex !== -1 && scheduledDateIndices.includes(todayIndex)) {
      this.scheduledDateIndex = todayIndex;
    } else {
      this.scheduledDateIndex = scheduledDateIndices[0];
    }
    
    const dateOption = this.dateOptions[this.scheduledDateIndex];
    if (dateOption) {
      this.updateScheduledDateProperties(dateOption, mealCountMap);
    }
  }

  private updateScheduledDateProperties(dateOption: DateCardOption, mealCountMap: Map<string, number>): void {
    this.scheduledDayLabel = dateOption.dayLabel;
    this.scheduledDateLabel = dateOption.dateLabel;
    const dayNumber = dateOption.dateLabel.split(' ')[0];
    const monthName = this.getMonthNameForDateValue(dateOption.dateValue);
    this.formattedScheduledDate = `Today - ${dateOption.dayLabel} ${dayNumber}, ${monthName}`;
    this.mealCount = (dateOption.dateValue && mealCountMap.has(dateOption.dateValue))
      ? (mealCountMap.get(dateOption.dateValue) ?? 0)
      : 0;
  }

  private handleNoScheduledDates(): void {
    const wasTodaySelected = this.isTodaySelected;
    // Don't override booking status as it's already calculated in updateBookingStatus
    this.isTodaySelected = false;
    
    const todayOption = this.getTodayOption();
    if (todayOption) {
      this.setScheduledDateFromOption(todayOption);
      this.mealCount = this.extractMealCountForDate(todayOption.dateValue);
    } else {
      this.setFallbackScheduledDate();
    }
    
    this.updatePrebookingDates();
    this.getUserCount();
    
    if (wasTodaySelected) {
      this.isTodaySelected = true;
    }
  }

  private setDefaultScheduledDate(): void {
    this.isTodaySelected = false;
    
    const todayOption = this.getTodayOption();
    if (todayOption) {
      this.setScheduledDateFromOption(todayOption);
      this.mealCount = this.extractMealCountForDate(todayOption.dateValue);
    } else {
      this.setFallbackScheduledDate();
    }
    
    // When no meal data, all days are not booked
    this.allDaysNotBooked = true;
    this.hasFutureDaysBooked = false;
    this.bookedFutureDays = [];
    
    this.updatePrebookingDates();
    this.getUserCount();
  }

  private getTodayOption(): DateCardOption | null {
    const todayIndex = this.dateOptions.findIndex(option => option.dateValue === this.todayDateValue);
    if (todayIndex !== -1) {
      return this.dateOptions[todayIndex];
    }
    return this.dateOptions[0] ?? null;
  }

  private setScheduledDateFromOption(option: DateCardOption): void {
    const todayIndex = this.dateOptions.findIndex(opt => opt.dateValue === this.todayDateValue);
    this.scheduledDateIndex = todayIndex !== -1 ? todayIndex : 0;
    this.scheduledDayLabel = option.dayLabel;
    this.scheduledDateLabel = option.dateLabel;
    const dayNumber = option.dateLabel.split(' ')[0];
    const monthName = this.getMonthNameForDateValue(option.dateValue);
    this.formattedScheduledDate = `Today - ${option.dayLabel} ${dayNumber}, ${monthName}`;
  }

  private getMonthNameForDateValue(dateValue?: string): string {
    const parsed = dateValue ? FormDateUtils.parseDateOnly(dateValue) : null;
    if (parsed) {
      return parsed.toLocaleDateString('en-US', { month: 'long' });
    }
    return new Date().toLocaleDateString('en-US', {
      timeZone: HOUSTON_TIME_ZONE,
      month: 'long',
    });
  }

  private setFallbackScheduledDate(): void {
    this.scheduledDateIndex = 0;
    this.scheduledDayLabel = "Today";
    this.scheduledDateLabel = "";
    this.formattedScheduledDate = "Today - Today";
    this.mealCount = 0;
  }

  private extractMealCountForDate(dateValue?: string): number {
    if (!this.mealData || !dateValue) {
      return 0;
    }
    
    const mealCountMap = this.buildMealCountMap();
    return mealCountMap.get(dateValue) ?? 0;
  }

  private buildMealCountMap(): Map<string, number> {
    const mealCountMap = new Map<string, number>();
    
    if (this.mealData?.results && Array.isArray(this.mealData.results)) {
      this.mealData.results.forEach((item: MealDataItem) => {
        if (item.mealDate && item.count !== undefined) {
          mealCountMap.set(item.mealDate, item.count);
        }
      });
    } else if (Array.isArray(this.mealData)) {
      (this.mealData as unknown as MealDataItem[]).forEach((item: MealDataItem) => {
        const dateValue = item.mealDate ?? item.date;
        if (dateValue && item.count !== undefined) {
          mealCountMap.set(dateValue, item.count);
        }
      });
    }
    
    return mealCountMap;
  }

  onBackClick(): void {
    this.router.navigate(['/card-checkin']);
  }

  onNextClick(): void {
    // Continue check-in: book activities for today, then events / done.
    const todayValue = this.dateOptions[0]?.dateValue;
    if (todayValue) {
      sessionStorage.setItem('dateValue', todayValue);
    }
    sessionStorage.removeItem('editFromSuccess');
    this.router.navigate(['/activity-screen']);
  }

  onPrebookingDateClick(dateIndex: number): void {
    this.openEditPlanForDate(dateIndex);
  }

  onEditPlanClick(dateIndex: number): void {
    this.openEditPlanForDate(dateIndex);
  }

  private openEditPlanForDate(dateIndex: number): void {
    // Set the current editing date so meal preference knows which date is being selected
    this.dateSelectionService.setCurrentEditingDate(dateIndex);
    
    // Get the dateValue for the selected date
    const selectedDate = this.dateOptions[dateIndex];
    if (selectedDate && selectedDate.dateValue) {
      sessionStorage.setItem('dateValue', selectedDate.dateValue);
    }
    
    // Store current route so meal-preferance returns here after save (Edit Plan without full back-flow)
    sessionStorage.setItem('previousRoute', '/success-meal-transport');
    
    // Navigate to meal preference to select meal and transport for this date
    this.router.navigate(['/meal-preferance'], { 
      queryParams: { date: selectedDate?.dateValue ?? '' } 
    });
  }

  onPrebookingDateKeyDown(event: KeyboardEvent, dateIndex: number): void {
    // Handle Enter and Space keys for keyboard navigation
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onPrebookingDateClick(dateIndex);
    }
  }

  private getUserCount(): void {
    getUserCountCommon(
      this.userService,
      this.dateOptions,
      (updatedOptions, response) => {
        this.userCount = response;
        
        // Only update badgeCount for dates that do NOT have meal info
        // For dates with meal info, preserve the count from mealData
        const updatedMap = new Map<string, DateOptionWithBadge>();
        updatedOptions.forEach(option => {
          if (option.dateValue) {
            updatedMap.set(option.dateValue, option);
          }
        });

        // Create a set of dates that have an actual meal preference (not cleared rows)
        const mealDates = new Set<string>();
        if (this.mealData?.results && Array.isArray(this.mealData.results)) {
          this.mealData.results.forEach((item: MealDataItem) => {
            if (item.mealDate && this.hasFullSchedule(item)) {
              mealDates.add(item.mealDate);
            }
          });
        } else if (Array.isArray(this.mealData)) {
          (this.mealData as unknown as MealDataItem[]).forEach((item: MealDataItem) => {
            if (!this.hasFullSchedule(item)) {
              return;
            }
            if (item.mealDate) {
              mealDates.add(item.mealDate);
            } else if (item.date) {
              mealDates.add(item.date);
            }
          });
        }

        this.dateOptions = this.dateOptions.map((currentOption) => {
          const updatedOption = (currentOption.dateValue && updatedMap.get(currentOption.dateValue)) ?? null;
          const hasMealInfo = currentOption.dateValue && mealDates.has(currentOption.dateValue);
          
          if (updatedOption) {
            // Only update badgeCount if the date does NOT have meal info
            // For dates with meal info, preserve the count from mealData
            if (!hasMealInfo && updatedOption.badgeCount !== undefined) {
              return {
                ...currentOption,
                badgeCount: updatedOption.badgeCount
              } as DateCardOption;
            }
          }
          return currentOption;
        });
        
        // Update prebookingDates with badge counts and state
        this.prebookingDates = this.prebookingDates.map((date) => {
          const matchedOption = this.dateOptions.find(option => option.dateValue === date.dateValue);
          return {
            ...date,
            badgeCount: matchedOption?.badgeCount ?? 0,
            state: matchedOption?.state ?? date.state
          };
        });
      }
    );
  }
}
